AFRAME.registerComponent("asset-metadata", {
  schema: {
    // Basic fields
    assetId: { type: "string" },
    assetName: { type: "string" },
    assetDescription: { type: "string" },
    assetUrl: { type: "string" },
    assetType: { type: "string" },
    assetCategory: { type: "string", default: "file" },
    isExternalAsset: { type: "boolean", default: false },
    previewUrl: { type: "string" },

    // Prefab properties
    prefabSourceId: { type: "string" },
    isPrefabInstance: { type: "boolean", default: false },

    // Work Information
    artworkYear: { type: "string" },
    medium: { type: "string" },
    customMedium: { type: "string" },
    mediumObject: {
      type: "string",
      parse: v => (typeof v === "object" ? v : v ? JSON.parse(v) : null),
      stringify: v => (v ? JSON.stringify(v) : "")
    },
    frame: {
      type: "string",
      parse: v => (typeof v === "object" ? v : v ? JSON.parse(v) : null),
      stringify: v => (v ? JSON.stringify(v) : "")
    },
    dimensionHeight: { type: "string" },
    dimensionWidth: { type: "string" },
    dimensionDepth: { type: "string" },
    digitizationTechnique: { type: "string" },
    currentState: { type: "string" },
    labels: { type: "string" },
    enableAR: { type: "boolean", default: false },

    // Artist Information
    artistName: { type: "string" },
    artistDate: { type: "string" },
    artistBio: { type: "string" },
    artistStatement: { type: "string" },

    // Legal and Rights
    useLicense: { type: "string" },
    isOriginal: { type: "boolean", default: false },
    aiCreated: { type: "boolean", default: false },
    aiTool: { type: "string" },
    acceptTerms: { type: "boolean", default: false }
  },

  init() {
    // Component initialization
    this.parentEl = this.el; // Reference self, not parent - component is now at same level as media-loader
    this.isDigitalArt = this.data.assetCategory === "digital-art";
    this.isDigitalTwinArt = this.data.assetCategory === "digital-twin-art";
    this.isArtwork = this.isDigitalArt || this.isDigitalTwinArt;
    this.artworkApplied = false;
    this.artworkMethodsTriggered = false; // Prevent duplicate method calls
    this.initialized = true; // Track that init has run

    // Check if media is already loaded (component added after media-loaded event)
    const mediaLoaderComponent = this.parentEl.components["media-loader"];
    const isMediaAlreadyLoaded = mediaLoaderComponent && mediaLoaderComponent.loaded;

    if (this.isArtwork && this.parentEl) {
      if (isMediaAlreadyLoaded) {
        // Media is already loaded, apply artwork immediately
        this.applyArtworkToParent();
        this.artworkApplied = true;
      } else {
        // Wait for media to load
        this.waitForParentMediaThenApplyArtwork();
      }
    }

    // Emit metadata loaded event
    this.el.emit("asset-metadata-loaded", { metadata: this.data });
  },

  waitForParentMediaThenApplyArtwork() {
    // Listen for media-loaded event from media-loader (both editor and sidebar use this)
    const onMediaLoaded = () => {
      if (!this.artworkApplied) {
        // Apply artwork after media-loader completes
        this.applyArtworkToParent();
        this.artworkApplied = true;
      }
    };

    this.parentEl.addEventListener("media-loaded", onMediaLoaded, { once: true });

    // Timeout fallback in case event doesn't fire (unlikely but safe)
    setTimeout(() => {
      if (!this.artworkApplied) {
        console.warn("asset-metadata: media-loaded event timeout, attempting to apply artwork anyway");
        this.applyArtworkToParent();
        this.artworkApplied = true;
      }
    }, 5000); // 5 second timeout
  },

  update(oldData) {
    // Skip update during init (let media-loaded handle it)
    if (!this.initialized) return;

    // Re-apply artwork if category changed after init
    if (this.data.assetCategory !== oldData.assetCategory) {
      this.isDigitalArt = this.data.assetCategory === "digital-art";
      this.isDigitalTwinArt = this.data.assetCategory === "digital-twin-art";
      this.isArtwork = this.isDigitalArt || this.isDigitalTwinArt;
      this.artworkApplied = false; // Reset flag to allow re-application
      this.artworkMethodsTriggered = false; // Reset methods flag too

      if (this.isArtwork && this.parentEl) {
        // Check if media is already loaded
        const mediaLoaderComponent = this.parentEl.components["media-loader"];
        const isMediaAlreadyLoaded = mediaLoaderComponent && mediaLoaderComponent.loaded;

        if (isMediaAlreadyLoaded) {
          // Media is already loaded, apply artwork immediately
          this.applyArtworkToParent();
          this.artworkApplied = true;
        } else {
          // Wait for media to load
          this.waitForParentMediaThenApplyArtwork();
        }
      } else if (!this.isArtwork && this.parentEl.components["artwork"]) {
        // Changed FROM artwork to file, remove artwork component and all its children
        // Remove all artwork-related child entities first
        const children = Array.from(this.parentEl.children || []);
        children.forEach(child => {
          if (child.components) {
            if (
              child.components["artwork-info"] ||
              child.components["artwork-frame"] ||
              child.components["artwork-medium"] ||
              child.components["artwork-qr"]
            ) {
              child.remove();
            }
          }
        });

        // Show original media-image mesh if it was hidden by medium
        if (this.parentEl.object3DMap && this.parentEl.object3DMap.mesh) {
          this.parentEl.object3DMap.mesh.visible = true;
        }

        // Remove artwork component and related components
        this.parentEl.removeAttribute("artwork");
        // this.parentEl.removeAttribute("artwork-toggler-menu"); // Already removed/not added
        this.parentEl.removeAttribute("inspect-pivot-child-selector");
      }
      return; // Category change triggers full reapplication, skip field-level updates
    }

    // If artwork is already applied, update the artwork component when relevant fields change
    if (this.artworkApplied && this.isArtwork && this.parentEl.components["artwork"]) {
      // Check if any artwork-relevant fields changed
      const fieldsToCheck = [
        "artworkName",
        "artworkDescription",
        "artistName",
        "artistDate",
        "artistBio",
        "artistStatement",
        "artworkYear",
        "medium",
        "customMedium",
        "mediumObject",
        "frame",
        "dimensionHeight",
        "dimensionWidth",
        "dimensionDepth",
        "digitizationTechnique",
        "currentState",
        "isOriginal",
        "aiCreated",
        "aiTool",
        "acceptTerms"
      ];

      const hasChanges = fieldsToCheck.some(field => this.data[field] !== oldData[field]);

      if (hasChanges) {
        this.updateArtworkComponent();
      }
    }
  },

  updateArtworkComponent() {
    // Build artworkInfo from current data
    const widthInches = this.parseDimensionInches(this.data.dimensionWidth);
    const heightInches = this.parseDimensionInches(this.data.dimensionHeight);
    const depthInches = this.parseDimensionInches(this.data.dimensionDepth);

    const dimensionsText = this.buildDimensionsText(widthInches, heightInches, depthInches);

    const artworkInfo = {
      title: this.data.artworkName || "",
      author: this.data.artistName || "",
      description: this.data.artworkDescription || "",
      artworkDate: this.data.artworkYear || "",
      medium: this.data.medium || "",
      dimensions: dimensionsText,
      dimensionsX: widthInches,
      dimensionsY: heightInches,
      dimensionsZ: depthInches,
      artistStatement: this.data.artistStatement || "",
      metadata: {
        customMedium: this.data.customMedium || "",
        medium: this.data.mediumObject || null,
        frame: this.data.frame || null,
        artistBio: this.data.artistBio || "",
        authorDate: this.data.artistDate || "",
        declareRights: this.data.isOriginal || false,
        workCreatedByAI: this.data.aiCreated || false,
        aiToolUsed: this.data.aiTool || "",
        terms: this.data.acceptTerms || false,
        techniqueUsed: this.data.digitizationTechnique || "",
        currentPhysicalWork: this.data.currentState || ""
      }
    };

    // Get current artwork component data
    const artworkComponent = this.parentEl.components["artwork"];
    if (!artworkComponent) return;

    // Get image URL from media component
    let imageUrl = "";
    if (this.parentEl.components["media-image"]) {
      imageUrl = this.parentEl.components["media-image"].data.src;
    }

    // Parse frame and mediumObject if they are JSON strings (from asset-metadata schema)
    let frameData = this.data.frame;
    let mediumData = this.data.mediumObject;

    // Handle frame parsing (can be string JSON, object, or null)
    if (frameData && typeof frameData === "string") {
      try {
        const parsed = JSON.parse(frameData);
        frameData = parsed; // Could be null, object, or string
      } catch (e) {
        console.warn("asset-metadata: Failed to parse frame data in updateArtworkComponent:", e);
        frameData = null;
      }
    }
    // If frameData is null or undefined, keep it as null
    if (!frameData) {
      frameData = null;
    }

    // Handle medium parsing (can be string JSON, object, or null)
    if (mediumData && typeof mediumData === "string") {
      try {
        const parsed = JSON.parse(mediumData);
        mediumData = parsed; // Could be null, object, or string
      } catch (e) {
        console.warn("asset-metadata: Failed to parse mediumObject data in updateArtworkComponent:", e);
        mediumData = null;
      }
    }
    // If mediumData is null or undefined, keep it as null
    if (!mediumData) {
      mediumData = null;
    }

    const mediumName = this.data.medium || this.data.customMedium || "Digital File";
    const shouldUseMedium = mediumData?.model && mediumName !== "Digital File" && mediumName !== "Custom";

    // Extract frame URL/model (handle both object and string formats)
    let frameUrl = null;
    if (frameData) {
      if (typeof frameData === "object" && frameData !== null) {
        frameUrl = frameData.model || frameData.url || null;
      } else if (typeof frameData === "string") {
        frameUrl = frameData;
      }
    }

    // Extract medium model URL
    const mediumModelUrl = shouldUseMedium && mediumData?.model ? mediumData.model : null;

    // Update artwork component - this triggers artwork.js update() lifecycle
    this.parentEl.setAttribute("artwork", {
      artworkInfo: artworkInfo,
      frame: frameUrl,
      mediumName: mediumName,
      glbModel: mediumModelUrl,
      image: imageUrl,
      isDigitalFile: this.isDigitalArt,
      hideArtworkLabel: false,
      isArtwork: true
    });
  },

  applyArtworkToParent() {
    try {
      if (this.artworkApplied) {
        return;
      }

      // Verify parent has object3DMap.mesh before applying artwork
      if (!this.parentEl.object3DMap || !this.parentEl.object3DMap.mesh) {
        console.warn("asset-metadata: Parent does not have object3DMap.mesh, cannot apply artwork");
        return;
      }
    } catch (error) {
      console.error("asset-metadata: Error in applyArtworkToParent initial check:", error);
      return;
    }

    // Build artworkInfo object from metadata
    const artworkInfo = this.buildArtworkInfo();

    // Get image URL from parent's media component
    const imageUrl = this.getParentMediaUrl();

    // Parse frame and mediumObject if they are JSON strings (from asset-metadata schema)
    let frameData = this.data.frame;
    let mediumData = this.data.mediumObject;

    if (typeof frameData === "string" && frameData) {
      try {
        frameData = JSON.parse(frameData);
      } catch (e) {
        console.warn("asset-metadata: Failed to parse frame data:", e);
        frameData = null;
      }
    }

    if (typeof mediumData === "string" && mediumData) {
      try {
        mediumData = JSON.parse(mediumData);
      } catch (e) {
        console.warn("asset-metadata: Failed to parse mediumObject data:", e);
        mediumData = null;
      }
    }

    // Skip medium 3D model for "Digital File" and "Custom" mediums (they don't have 3D models)
    const mediumName = this.data.medium || this.data.customMedium || "Digital File";
    const shouldUseMedium = mediumData?.model && mediumName !== "Digital File" && mediumName !== "Custom";

    const artworkData = {
      artworkId: this.data.assetId,
      image: imageUrl,
      artworkInfo: artworkInfo, // Will be stringified by AFRAME
      frame: frameData?.model || frameData?.url || null,
      mediumName: mediumName,
      glbModel: shouldUseMedium ? mediumData.model : null, // Medium 3D model URL (null for Digital File/Custom)
      isDigitalFile: this.isDigitalArt,
      hideArtworkLabel: false,
      isArtwork: true // Flag to trigger artwork behavior
    };

    try {
      // Check if artwork component is registered
      if (!AFRAME.components["artwork"]) {
        console.error("[asset-metadata] Artwork component is not registered in AFRAME!");
        return;
      }

      // Apply artwork component to parent
      this.parentEl.setAttribute("artwork", artworkData);

      // Mark as applied immediately to prevent duplicate calls
      this.artworkApplied = true;

      // Force update artwork info and frame by triggering methods
      // Only call once to prevent duplicates
      // Capture frameData in closure for setTimeout
      const hasFrame = !!frameData;
      setTimeout(() => {
        if (this.artworkMethodsTriggered) return; // Prevent duplicate calls
        this.artworkMethodsTriggered = true;

        const artworkComponent = this.parentEl.components["artwork"];

        if (artworkComponent) {
          // updateArtworkInfo creates the artwork-info child entity
          if (typeof artworkComponent.updateArtworkInfo === "function") {
            artworkComponent.updateArtworkInfo();
          }

          // updateFrame creates the frame if needed
          if (typeof artworkComponent.updateFrame === "function" && hasFrame) {
            artworkComponent.updateFrame();
          }

          // Don't call createScene - updateArtworkInfo already creates the label
        } else {
          console.warn("[asset-metadata] artwork component not found on parent after setAttribute");
        }
      }, 500);

      // Add artwork-specific components
      // this.parentEl.setAttribute("artwork-toggler-menu", {}); // Removed: now only info button is clickable
      this.parentEl.setAttribute("inspect-pivot-child-selector", {
        selector: ".interactable-ui"
      });

      // Ensure artwork can be targeted by remote/cursor interactions
      this.parentEl.setAttribute("is-remote-hover-target", "");
      this.parentEl.setAttribute("css-class", "interactable");
      let tagsData = this.parentEl.getAttribute("tags");
      if (!tagsData || typeof tagsData !== "object") {
        tagsData = {};
      }
      if (!tagsData.isHandCollisionTarget) {
        tagsData.isHandCollisionTarget = true;
      }
      this.parentEl.setAttribute("tags", tagsData);
      if (!this.parentEl.hasAttribute("hoverable-visuals")) {
        this.parentEl.setAttribute("hoverable-visuals", "");
      }
    } catch (error) {
      console.error("asset-metadata: Error applying artwork to parent:", error);
    }
  },

  buildArtworkInfo() {
    // Build ArtworkInfo structure (matches artwork-model-utils.js)
    // Use artworkName for artwork categories, fallback to assetName for compatibility
    const titleToUse = this.data.artworkName || this.data.assetName || "";
    const descriptionToUse = this.data.artworkDescription || this.data.assetDescription || "";
    const widthInches = this.parseDimensionInches(this.data.dimensionWidth);
    const heightInches = this.parseDimensionInches(this.data.dimensionHeight);
    const depthInches = this.parseDimensionInches(this.data.dimensionDepth);

    return {
      title: titleToUse,
      author: this.data.artistName,
      authorBirthDeath: this.data.artistDate,
      description: descriptionToUse,
      artistStatement: this.data.artistStatement,
      artworkDate: this.data.artworkYear,
      medium: this.data.medium === "Other" ? this.data.customMedium : this.data.medium,
      dimensions: this.buildDimensionsText(widthInches, heightInches, depthInches),
      dimensionsX: widthInches,
      dimensionsY: heightInches,
      dimensionsZ: depthInches,
      category: this.data.assetCategory,
      metadata: {
        customMedium: this.data.customMedium,
        // Map to sidebar expected field names
        techniqueUsed: this.data.digitizationTechnique,
        currentPhysicalWork: this.data.currentState,
        useLicense: this.data.useLicense,
        workCreatedByAI: this.data.aiCreated,
        aiToolUsed: this.data.aiTool,
        artistBio: this.data.artistBio
      }
    };
  },

  formatDimensions() {
    const widthInches = this.parseDimensionInches(this.data.dimensionWidth);
    const heightInches = this.parseDimensionInches(this.data.dimensionHeight);
    const depthInches = this.parseDimensionInches(this.data.dimensionDepth);
    return this.buildDimensionsText(widthInches, heightInches, depthInches);
  },
  parseDimensionInches(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }

    const trimmed = String(value).trim();
    if (trimmed === "") return null;

    // Helper to parse fractions like "29 5/8" or "5/8"
    const parseFraction = str => {
      // Match mixed fraction like "29 5/8"
      const mixedFractionMatch = str.match(/(\d+)\s+(\d+)\/(\d+)/);
      if (mixedFractionMatch) {
        const whole = parseFloat(mixedFractionMatch[1]);
        const numerator = parseFloat(mixedFractionMatch[2]);
        const denominator = parseFloat(mixedFractionMatch[3]);
        return whole + numerator / denominator;
      }

      // Match simple fraction like "5/8"
      const simpleFractionMatch = str.match(/(\d+)\/(\d+)/);
      if (simpleFractionMatch) {
        const numerator = parseFloat(simpleFractionMatch[1]);
        const denominator = parseFloat(simpleFractionMatch[2]);
        return numerator / denominator;
      }

      return null;
    };

    // Try to parse as fraction first
    const fractionValue = parseFraction(trimmed);
    if (fractionValue !== null) {
      return fractionValue;
    }

    const inchesPattern = /^([\d.]+)\s*(?:in|inch|inches|")?$/i;
    const cmPattern = /^([\d.]+)\s*cm$/i;

    if (inchesPattern.test(trimmed)) {
      const match = trimmed.match(inchesPattern);
      const num = Number(match[1]);
      return Number.isFinite(num) ? num : null;
    }

    if (cmPattern.test(trimmed)) {
      const match = trimmed.match(cmPattern);
      const num = Number(match[1]);
      if (Number.isFinite(num)) {
        return +(num / 2.54);
      }
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  },
  buildDimensionsText(widthInches, heightInches, depthInches) {
    const format = value => {
      if (!Number.isFinite(value)) return null;
      const rounded = Math.round(value * 100) / 100;
      return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2).replace(/\.?0+$/, "");
    };

    const parts = [];
    // Museum convention: HEIGHT × WIDTH × DEPTH (first value is height)
    const h = format(heightInches);
    const w = format(widthInches);
    const d = format(depthInches);

    if (h) parts.push(h);
    if (w) parts.push(w);
    if (d) parts.push(d);

    return parts.length > 0 ? `${parts.join(" x ")} in` : "";
  },

  getParentMediaUrl() {
    // Get media URL from parent's component
    if (!this.parentEl) return null;

    if (this.parentEl.components["media-image"]) {
      return this.parentEl.components["media-image"].data.src;
    }
    if (this.parentEl.components["media-video"]) {
      return this.parentEl.components["media-video"].data.src;
    }
    if (this.parentEl.components["gltf-model-plus"]) {
      return this.parentEl.components["gltf-model-plus"].data.src;
    }

    return this.data.assetUrl;
  },

  // Helper methods for external access
  getMetadata() {
    return {
      ...this.data,
      isArtwork: this.isArtwork,
      artworkInfo: this.isArtwork ? this.buildArtworkInfo() : null
    };
  },

  isPrefab() {
    return this.data.prefabSourceId !== null && this.data.prefabSourceId !== "";
  },

  getPrefabSourceId() {
    return this.data.prefabSourceId;
  }
});
